import { Button, Checkbox, Divider, Input, message, Tooltip } from "antd";
import { ErrorMessages } from "constants/index";
import { FeatureIdTemplateMapping, FeatureTypes } from "entity-app/constants";
import { useMemo, useState } from "react";
import { useParams } from "react-router";

export default function FeatureDuplicate(props: {
  currentFeatureType: FeatureTypes;
  featureInstanceData: any;
  actions?: any;
  linkedSourcefeatureType: FeatureTypes;
  setVisible?: (open: boolean) => void;
  visible?: boolean;
}) {
  const {
    currentFeatureType,
    featureInstanceData,
    actions,
    linkedSourcefeatureType,
    setVisible,
    visible
  } = props;
  const [isIncludeLinks, setInduleLinks] = useState({
    features: false,
    activities: false
  });
  const { projectId } = useParams() as any;

  const [copiedName, setCopyName] = useState<string>(
    `${featureInstanceData?.title} (copy)`
  );

  const featureName = useMemo(() => {
    return FeatureIdTemplateMapping[currentFeatureType] || "";
  }, [currentFeatureType]);

  const featureLinks = useMemo(() => {
    switch (linkedSourcefeatureType) {
      case FeatureTypes.SUBMITTALS:
        return featureInstanceData?.links_as_target?.filter(
          (link: any) =>
            link?.source_feature_instance?.feature_type_id ===
            FeatureTypes.SUBMITTALS
        );
      case FeatureTypes.MATERIALS:
        return featureInstanceData?.links_as_source?.filter(
          (link: any) =>
            link?.target_feature_instance?.feature_type_id ===
            FeatureTypes.MATERIALS
        );

      default:
        return [];
    }
  }, [
    featureInstanceData?.links_as_source,
    featureInstanceData?.links_as_target,
    linkedSourcefeatureType
  ]);

  const featureActivityLinks = useMemo(() => {
    return featureInstanceData?.links_as_source?.filter(
      (link: any) =>
        link?.target_feature_instance?.feature_type_id ===
        FeatureTypes.ACTIVITIES
    );
  }, [featureInstanceData?.links_as_source]);

  const featureLinkedTitles = () => {
    switch (currentFeatureType) {
      case FeatureTypes.SUBMITTALS:
        return {
          sourceTitle: "Linked Materials",
          targetTitle: "Linked Activities"
        };
      case FeatureTypes.MATERIALS:
        return {
          sourceTitle: "Linked Submittals",
          targetTitle: "Linked Activities"
        };
      default:
        return {
          sourceTitle: "",
          targetTitle: ""
        };
    }
  };

  const messageText = (newFeatureId: string) => {
    const feature = `${featureName}s`.toLowerCase();
    return (
      <div>
        {`Duplicate ${featureName} created. Click`}
        <span className="mx-1">
          <a
            href={`/project/${projectId}/${feature}/${newFeatureId}`}
            className="underline"
            data-testid="index-link"
          >
            here
          </a>
        </span>
        to view details.
      </div>
    );
  };

  if (!featureInstanceData) return null;

  return (
    <div className="flex flex-col text-xs w-72 space-y-2">
      <div className="flex text-xs">
        {`Provide ${featureName} Name for the duplicate.`}
      </div>
      <div className="flex items-center">
        <div className="font-semibold pr-1 w-14">Name : </div>
        <Input
          type="text"
          className="grow"
          value={copiedName}
          onChange={(env) => {
            const name = env.target.value;
            setCopyName(name);
          }}
          data-testid="index-input-name"
        />
      </div>
      <div className="flex flex-col text-xs pt-4 pb-1 space-y-2">
        <div>Do you want to copy the links from original ?</div>
        <div className="flex justify-between">
          <Tooltip
            title={
              featureLinks?.length < 1 ? ErrorMessages.NoLinksPresent : null
            }
          >
            <Checkbox
              className="text-xs"
              checked={featureLinks?.length > 0 && isIncludeLinks.features}
              disabled={!featureLinks?.length}
              onChange={(event) => {
                setInduleLinks((pre) => ({
                  ...pre,
                  features: event.target.checked
                }));
              }}
            >
              {featureLinkedTitles().sourceTitle}
            </Checkbox>
          </Tooltip>
          <Tooltip
            title={
              featureActivityLinks?.length < 1
                ? ErrorMessages.NoLinksPresent
                : null
            }
          >
            <Checkbox
              className="text-xs"
              checked={
                featureActivityLinks?.length > 0 && isIncludeLinks.activities
              }
              disabled={!featureActivityLinks?.length}
              onChange={(event) => {
                setInduleLinks((pre) => ({
                  ...pre,
                  activities: event.target.checked
                }));
              }}
            >
              {featureLinkedTitles().targetTitle}
            </Checkbox>
          </Tooltip>
        </div>
      </div>
      <Divider className="py-1" />

      <div className="flex justify-end space-x-2 text-xs">
        <Button
          onClick={() => {
            if (setVisible) {
              setVisible(false);
            }
          }}
          data-testid="index-button-cancel"
        >
          Cancel
        </Button>
        <Button
          type="primary"
          disabled={copiedName === ""}
          data-testid="index-button-create"
          onClick={async () => {
            if (setVisible) {
              setVisible(!visible);
            }
            const type: any[] = [];
            if (isIncludeLinks.activities && isIncludeLinks.features) {
              type.push(2, 9);
            } else if (isIncludeLinks.activities && !isIncludeLinks.features) {
              type.push(9);
            } else if (isIncludeLinks.features && !isIncludeLinks.activities) {
              type.push(2);
            }
            const linksParams = () => {
              switch (currentFeatureType) {
                case FeatureTypes.SUBMITTALS:
                  return {
                    linked_source_feature_types: [],
                    linked_target_feature_types: type
                  };
                case FeatureTypes.MATERIALS:
                  return {
                    linked_source_feature_types: isIncludeLinks.features
                      ? [1]
                      : [],
                    linked_target_feature_types: isIncludeLinks.activities
                      ? [9]
                      : []
                  };

                default:
                  return {};
              }
            };

            const payload = {
              ...linksParams(),
              title: copiedName,
              feature_instance_id: featureInstanceData?.id
            };
            const response = await actions?.onDuplicateFeature(payload);
            if (response.success) {
              message.success(
                messageText(response.data?.duplicate_feature_instance?.id),
                10
              );
            }
            if (response.errors) {
              message.error(response.errors[0].message);
            }
          }}
        >
          Create
        </Button>
      </div>
    </div>
  );
}
